import { useTranslation } from 'react-i18next';
import { useBilling } from '@/features/billing/hooks/useBilling';
import {
  formatElapsedPlanPeriodDate,
  formatPlanPeriodDate,
} from '@/features/billing/lib/formatPlanPeriodDate';
import {
  BUSINESS_ANNUAL,
  FAMILY_ANNUAL,
} from '@/features/billing/lib/getRedirectUrl';
import {
  BUSINESS_INCLUDED_SEATS,
  BUSINESS_SEAT_PACK_SIZE,
} from '@/features/billing/lib/seatPacks';
import {
  isSubscriptionInterrupted,
  subscriptionHealthDate,
  type SubscriptionHealth,
} from '@/features/billing/lib/subscriptionHealth';
import { SubscriptionPlan } from '@/types/gql.generated';
import { Box, Button, Text } from '@/ui';
import { PlanBadge } from '../../../components/PlanBadge';
import { PlanCard } from '../../../components/PlanCard';
import { ManageSubscriptionButton } from '../../Subscription/components/ManageSubscriptionButton';
import { ManualPlanSupportNotice } from '../../Subscription/components/ManualPlanSupportNotice';

type Props = {
  plan: SubscriptionPlan;
  health: SubscriptionHealth;
  /** BUSINESS orgs only: seat packs on top of the included seats. */
  seatPacks?: number | null;
  /** Owner-only capacity summary, e.g. "1 of 15 seats used". */
  usage?: string;
  /** Owners can open the billing portal; members see a read-only summary. */
  canManage: boolean;
};

// The workspace's own plan card. This is the home of the plan for a family or
// business org — the personal account panel defers here rather than showing
// billing twice.
export const OrganizationPlanSection = ({
  plan,
  health,
  seatPacks,
  usage,
  canManage,
}: Props) => {
  const { t } = useTranslation('settingsModal', { keyPrefix: 'organizations' });
  const { redirectToCheckout } = useBilling();
  const isBusiness = plan === SubscriptionPlan.Business;
  const planName =
    plan === SubscriptionPlan.Family
      ? t('plan.name_family')
      : isBusiness
        ? t('plan.name_business')
        : t('plan.name_plus');

  // Read off the health rather than taken as a separate prop: they come from the
  // same column, so two sources could disagree — and `none` has no date to show
  // even when the column is set, which is how a long-lapsed workspace used to
  // render "Active — Renews <a date in the past>".
  const periodEnd = subscriptionHealthDate(health);
  const formattedDate = formatPlanPeriodDate(periodEnd);
  const endedDate = formatElapsedPlanPeriodDate(periodEnd);

  // The plan clause is interpolated into the date sentence rather than appended
  // to it, so a translation controls where it lands and how it's punctuated.
  const detail = isBusiness
    ? t('plan.seat_composition', {
        count: seatPacks ?? 0,
        included: BUSINESS_INCLUDED_SEATS,
        packSize: BUSINESS_SEAT_PACK_SIZE,
      })
    : t('plan.family_access_note');

  // Every state the subscription can be in gets its own badge and sentence.
  // Previously this was one `isEnding = status === CANCELED` boolean, which
  // labelled a past-due workspace "Active — Renews <a date in the past>" and an
  // already-ended one "Ending" as though it hadn't happened yet.
  const { badgeKey, badgeTone, dateKey } = (() => {
    switch (health.state) {
      case 'dunning':
        return {
          badgeKey: 'plan.badge_past_due',
          badgeTone: 'attention',
          dateKey: 'plan.past_due',
        } as const;
      case 'lapsed':
        return {
          badgeKey: 'plan.badge_expired',
          badgeTone: 'attention',
          // An immediate cancellation leaves the period end unreached, so only
          // quote a date that has actually passed.
          dateKey: endedDate ? 'plan.expired' : 'plan.expired_no_date',
        } as const;
      case 'ending':
        return {
          badgeKey: 'plan.badge_ending',
          badgeTone: 'plan',
          dateKey: 'plan.ending',
        } as const;
      case 'manual':
        // No Stripe customer and no auto-renew, so "Renews" would be untrue.
        return {
          badgeKey: 'plan.badge_active',
          badgeTone: 'plan',
          dateKey: 'plan.subscribed',
        } as const;
      default:
        return {
          badgeKey: 'plan.badge_active',
          badgeTone: 'plan',
          dateKey: 'plan.renewal',
        } as const;
    }
  })();

  const isInterrupted = isSubscriptionInterrupted(health);
  // Manual (offline/check) grants have no Stripe customer, so the billing portal
  // would fail — swap the manage button for support.
  const isManual = health.state === 'manual';

  return (
    <PlanCard
      badge={<PlanBadge tone={badgeTone}>{t(badgeKey)}</PlanBadge>}
      title={planName}
      meta={
        usage && (
          <Text color="customgray.mid" fontSize="sm">
            {usage}
          </Text>
        )
      }
    >
      {/* An interrupted plan always explains itself; a healthy one only has
          something to say once there's a date to say it about. */}
      {(isInterrupted || formattedDate) && (
        <Text color="customgray.mid">
          {t(dateKey, { date: endedDate ?? formattedDate, detail })}
        </Text>
      )}

      {/* Nothing has been taken away — worth stating plainly, because the seat
          count and roster above still show everyone. */}
      {isInterrupted && (
        <Text color="customgray.mid">{t('plan.members_kept')}</Text>
      )}

      {canManage &&
        (isManual ? (
          <ManualPlanSupportNotice />
        ) : health.state === 'lapsed' ? (
          // Terminal: there is no subscription left for the portal to act on, so
          // this needs a fresh checkout. The server re-funds an owned-but-
          // defunded org in place rather than provisioning a duplicate, so the
          // roster and seat limit survive.
          <Box>
            <Button
              variant="secondary"
              onClick={() =>
                redirectToCheckout(
                  'yearly',
                  { launch: 'settings:organizations' },
                  isBusiness ? BUSINESS_ANNUAL : FAMILY_ANNUAL
                )
              }
            >
              {t('plan.reactivate')}
            </Button>
          </Box>
        ) : (
          <Box>
            <ManageSubscriptionButton variant="secondary">
              {t(
                health.state === 'dunning'
                  ? 'plan.update_payment'
                  : 'plan.manage'
              )}
            </ManageSubscriptionButton>
          </Box>
        ))}
    </PlanCard>
  );
};
